import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  CreditCard, 
  Wallet,
  Building,
  ChevronLeft,
  Loader2,
  CheckCircle,
  Shield,
  Users,
  UserPlus
} from 'lucide-react';
import { doctorService, appointmentService, couponService, patientService, getLocalizedValue, formatCurrencyAmount } from '@/services/api';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

const getPatientDisplayName = (patient, lang) => {
  if (!patient) return '';

  const firstName = getLocalizedValue(patient.first_name, lang) || patient.first_name?.en || patient.first_name || '';
  const lastName = getLocalizedValue(patient.last_name, lang) || patient.last_name?.en || patient.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || patient.name || '';
};

const BookingCheckoutPage = () => {
  const { doctorId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscountAmount, setCouponDiscountAmount] = useState(0);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientsLoading, setPatientsLoading] = useState(false);
  
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;
  
  // Get booking details from URL params
  const selectedDate = searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');
  const selectedTime = searchParams.get('time') || '10:00 AM';

  useEffect(() => {
    const fetchDoctor = async () => {
      setLoading(true);
      try {
        const response = await doctorService.getById(doctorId);
        setDoctor(response.data.data || response.data);
      } catch (error) {
        console.error('Failed to fetch doctor:', error);
        setDoctor(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctor();
  }, [doctorId]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const response = await patientService.getByUserId(user.id);
        const patientsData = response.data?.data || response.data || [];

        if (Array.isArray(patientsData) && patientsData.length > 0) {
          setPatients(patientsData);
          const defaultPatient = patientsData.find((patient) => patient.is_default) || patientsData[0];
          setSelectedPatient(defaultPatient);
        } else {
          setPatients([]);
          setSelectedPatient(null);
        }
      } catch (error) {
        console.error('Failed to fetch patients:', error);
        setPatients([]);
        setSelectedPatient(null);
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatients();
  }, [isAuthenticated, user?.id]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      toast.error(lang === 'ar' ? 'يرجى إدخال كود خصم' : 'Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    try {
      const response = await couponService.validate(code);
      const coupon = response?.data?.data || response?.data;
      const couponType = (coupon?.discount_type || coupon?.type || 'fixed').toLowerCase();
      const couponValue = Number(coupon?.discount ?? coupon?.value ?? 0);
      const discountValue = couponType === 'percent'
        ? (consultationFee * couponValue) / 100
        : couponValue;
      const normalizedDiscount = Math.max(0, Math.min(discountValue, consultationFee));

      setAppliedCoupon({
        code: coupon?.code || code,
        type: couponType,
        value: couponValue,
      });
      setCouponDiscountAmount(normalizedDiscount);
      toast.success(lang === 'ar' ? 'تم تطبيق الكوبون' : 'Coupon applied');
    } catch (error) {
      setAppliedCoupon(null);
      setCouponDiscountAmount(0);
      toast.error(
        error?.response?.data?.error ||
          (lang === 'ar' ? 'كود الخصم غير صالح' : 'Invalid coupon code')
      );
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error(lang === 'ar' ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      // Build the appointment payload to match Flutter mobile app structure
      // The backend expects full doctor, patient objects AND user_id at root level
      
      // Parse the selected time to create a proper datetime
      const appointmentDateTime = new Date(selectedDate);
      const timeParts = selectedTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (timeParts) {
        let hours = parseInt(timeParts[1]);
        const minutes = parseInt(timeParts[2]);
        const period = timeParts[3]?.toUpperCase();
        
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        
        appointmentDateTime.setHours(hours, minutes, 0, 0);
      }
      
      // Calculate end time (add session duration or default 30 minutes)
      const sessionDuration = doctor?.session_duration || doctor?.waiting_time || 30;
      const endDateTime = new Date(appointmentDateTime.getTime() + sessionDuration * 60000);
      
      // Get patient ID - user needs a patient profile
      const patient = selectedPatient || patients[0];
      const patientId = patient?.id || user?.patient_id || user?.default_patient_id;
      
      if (!patientId) {
        toast.error(
          lang === 'ar' ? 'يرجى إنشاء ملف مريض أولاً' : 'Please create a patient profile first',
          { description: lang === 'ar' ? 'انتقل إلى الإعدادات > الملف الشخصي' : 'Go to Settings > Profile' }
        );
        setSubmitting(false);
        return;
      }
      
      // Build the booking payload matching Flutter app structure + Laravel API expectations
      const bookingPayload = {
        doctor_id: doctor?.id || doctorId,
        patient_id: patientId,
        clinic_id: doctor?.clinic?.id || null,
        appointment_date: appointmentDateTime.toISOString(),
        // User ID at root level (REQUIRED by Laravel API)
        user_id: user?.id,
        // Doctor object (required by backend)
        doctor: {
          id: doctor?.id || doctorId,
          name: typeof doctor?.name === 'object' ? (doctor.name[lang] || doctor.name.ar || doctor.name.en || '') : (doctor?.name || ''),
          price: doctor?.price || doctor?.consultation_fee || 0,
          doctor_currency: doctor?.doctor_currency || doctor?.currency || 'SYP',
          currency: doctor?.currency || doctor?.doctor_currency || 'SYP',
          currency_symbol: doctor?.currency_symbol,
          available: doctor?.available ?? true,
          clinic_id: doctor?.clinic?.id || null,
        },
        // Patient object (required by backend)  
        patient: {
          id: patientId,
          user_id: user?.id,
          first_name: getLocalizedValue(patient?.first_name, lang) || patient?.first_name?.en || user?.first_name || user?.name?.split(' ')[0] || '',
          last_name: getLocalizedValue(patient?.last_name, lang) || patient?.last_name?.en || user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
          phone_number: patient?.phone_number || patient?.mobile_number || user?.phone_number || user?.phone || '',
        },
        // Clinic object (if doctor has a clinic)
        ...(doctor?.clinic && {
          clinic: {
            id: doctor.clinic.id,
            name: doctor.clinic.name || '',
          }
        }),
        // Appointment times
        appointment_at: appointmentDateTime.toISOString().replace('T', ' ').substring(0, 19),
        start_at: appointmentDateTime.toISOString().replace('T', ' ').substring(0, 19),
        ends_at: endDateTime.toISOString().replace('T', ' ').substring(0, 19),
        // Online or at clinic
        online: paymentMethod === 'online' || false,
        // Notes/hints
        hint: notes || '',
        // Coupon (if provided)
        ...(appliedCoupon?.code && {
          coupon: {
            code: appliedCoupon.code,
          }
        }),
      };
      
      console.log('Booking payload:', bookingPayload);
      
      await appointmentService.book(bookingPayload);
      
      toast.success(
        lang === 'ar' ? 'تم الحجز بنجاح!' : 'Booking confirmed!',
        { description: lang === 'ar' ? 'ستتلقى تأكيداً قريباً' : 'You will receive a confirmation shortly' }
      );
      navigate('/appointments');
    } catch (error) {
      console.error('Booking error:', error.response?.data || error);
      toast.error(
        lang === 'ar' ? 'فشل في الحجز' : 'Booking failed',
        { description: error.response?.data?.message || 'Please try again' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const consultationFee = doctor?.consultation_fee || doctor?.price || 0;
  const totalAmount = Math.max(consultationFee - couponDiscountAmount, 0);
  const currencySource = doctor || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full dark:bg-slate-800 border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              {lang === 'ar' ? 'تعذر تحميل بيانات الطبيب' : 'Doctor data could not be loaded'}
            </p>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-4 -ml-2"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {lang === 'ar' ? 'إتمام الحجز' : 'Complete Booking'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Appointment Summary */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">
                  {lang === 'ar' ? 'تفاصيل الموعد' : 'Appointment Details'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 pb-4 border-b">
                  <Avatar className="h-16 w-16 rounded-xl">
                    <AvatarImage src={doctor?.image} alt={doctor?.name} className="object-cover" />
                    <AvatarFallback className="rounded-xl bg-teal-600 text-white">
                      {doctor?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {lang === 'ar' && doctor?.name_ar ? doctor.name_ar : doctor?.name}
                    </h3>
                    <p className="text-sm text-teal-600">
                      {lang === 'ar' && doctor?.specialty?.name_ar ? doctor.specialty.name_ar : doctor?.specialty?.name}
                    </p>
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span>{format(new Date(selectedDate), 'EEE, MMM d', { locale })}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-slate-400" />
                    <span>{selectedTime}</span>
                  </div>
                  {doctor?.clinic?.name && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span>{doctor.clinic.name}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {isAuthenticated && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {lang === 'ar' ? 'حجز لـ' : 'Book for'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {patientsLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : patients.length === 0 ? (
                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {lang === 'ar' ? 'لا يوجد ملف مريض' : 'No patient profile'}
                      </span>
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/family" className="flex items-center gap-1">
                          <UserPlus className="h-3 w-3" />
                          {lang === 'ar' ? 'إضافة' : 'Add'}
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={selectedPatient?.id?.toString()}
                      onValueChange={(value) => {
                        const patient = patients.find((item) => item.id?.toString() === value);
                        setSelectedPatient(patient);
                      }}
                    >
                      <SelectTrigger data-testid="checkout-patient-selector">
                        <SelectValue placeholder={lang === 'ar' ? 'اختر المريض' : 'Select patient'}>
                          {getPatientDisplayName(selectedPatient, lang) || (selectedPatient?.id ? `Patient ${selectedPatient.id}` : '')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {patients.map((patient) => {
                          const name = getPatientDisplayName(patient, lang);
                          return (
                            <SelectItem key={patient.id} value={patient.id?.toString()}>
                              <div className="flex items-center gap-2">
                                <span>{name || `Patient ${patient.id}`}</span>
                                {patient.is_default && (
                                  <Badge variant="secondary" className="text-xs">
                                    {lang === 'ar' ? 'افتراضي' : 'Default'}
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">
                  {lang === 'ar' ? 'ملاحظات للطبيب' : 'Notes for Doctor'}
                </CardTitle>
                <CardDescription>
                  {lang === 'ar' 
                    ? 'صف أعراضك أو سبب الزيارة (اختياري)'
                    : 'Describe your symptoms or reason for visit (optional)'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder={lang === 'ar' ? 'اكتب ملاحظاتك هنا...' : 'Write your notes here...'}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  data-testid="notes-input"
                />
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">
                  {lang === 'ar' ? 'طريقة الدفع' : 'Payment Method'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="space-y-3">
                    <label 
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                        paymentMethod === 'cash' ? 'border-teal-500 bg-teal-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="cash" id="cash" />
                        <Wallet className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                        <div>
                          <p className="font-medium">{lang === 'ar' ? 'الدفع نقداً' : 'Pay Cash'}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {lang === 'ar' ? 'ادفع في العيادة' : 'Pay at the clinic'}
                          </p>
                        </div>
                      </div>
                    </label>
                    
                    <label 
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                        paymentMethod === 'card' ? 'border-teal-500 bg-teal-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="card" id="card" />
                        <CreditCard className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                        <div>
                          <p className="font-medium">{lang === 'ar' ? 'بطاقة ائتمان' : 'Credit Card'}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {lang === 'ar' ? 'ادفع الآن بالبطاقة' : 'Pay now with card'}
                          </p>
                        </div>
                      </div>
                    </label>

                    <label 
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                        paymentMethod === 'insurance' ? 'border-teal-500 bg-teal-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="insurance" id="insurance" />
                        <Building className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                        <div>
                          <p className="font-medium">{lang === 'ar' ? 'تأمين صحي' : 'Health Insurance'}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {lang === 'ar' ? 'استخدم التأمين الخاص بك' : 'Use your insurance'}
                          </p>
                        </div>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="dark:bg-slate-800 border-0 shadow-sm sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">
                  {lang === 'ar' ? 'ملخص الحجز' : 'Booking Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Coupon */}
                <div className="flex gap-2">
                  <Input
                    placeholder={lang === 'ar' ? 'كود الخصم' : 'Coupon code'}
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      if (!e.target.value.trim()) {
                        setAppliedCoupon(null);
                        setCouponDiscountAmount(0);
                      }
                    }}
                    className="flex-1"
                    data-testid="coupon-input"
                  />
                  <Button variant="outline" onClick={handleApplyCoupon} disabled={applyingCoupon}>
                    {applyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : (lang === 'ar' ? 'تطبيق' : 'Apply')}
                  </Button>
                </div>

                <Separator />

                {/* Price Breakdown */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">
                      {lang === 'ar' ? 'رسوم الاستشارة' : 'Consultation Fee'}
                    </span>
                    <span>{formatCurrencyAmount(consultationFee, currencySource)}</span>
                  </div>
                  {couponDiscountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>
                        {lang === 'ar' ? 'الخصم' : 'Discount'}
                        {appliedCoupon?.type === 'percent' ? ` (${appliedCoupon.value}%)` : ''}
                      </span>
                      <span>-{formatCurrencyAmount(couponDiscountAmount, currencySource)}</span>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex justify-between font-semibold text-lg">
                  <span>{lang === 'ar' ? 'الإجمالي' : 'Total'}</span>
                  <span className="text-teal-600">{formatCurrencyAmount(totalAmount, currencySource)}</span>
                </div>

                <Button 
                  className="w-full bg-teal-600 hover:bg-teal-700 mt-4" 
                  size="lg"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.loading')}</>
                  ) : (
                    <><CheckCircle className="h-4 w-4 mr-2" />{lang === 'ar' ? 'تأكيد الحجز' : 'Confirm Booking'}</>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-slate-500 mt-4">
                  <Shield className="h-4 w-4" />
                  <span>{lang === 'ar' ? 'دفع آمن ومشفر' : 'Secure & encrypted payment'}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCheckoutPage;
